import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  TrendingUp, 
  TrendingDown, 
  Save,
  RotateCcw,
  PiggyBank
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { addActivity } from '../utils/activityTracker';
import { calculateTaxAmount } from '../utils/taxData';

interface BudgetCategory {
  id: string;
  type: 'income' | 'expense';
  items: BudgetItem[];
}

interface BudgetItem {
  id: string;
  monthly: number;
  annual: number;
  isDefault?: boolean;
  defaultValue?: number;
}

const BudgetCalculatorApp: React.FC = () => {
  const { memberstack, isAuthenticated, isLoading } = useAuth();
  const { t } = useI18n();
  const [, setUserData] = useState<any>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Simple default values for when no budget data exists
  const getDefaultValues = (questionnaireData?: any): BudgetCategory[] => {
    console.log('Using simple default values', questionnaireData);
    
    const familyStatus = questionnaireData?.familyStatus || 'single';
    const isSingle = familyStatus === 'single';
    const childrenCount = questionnaireData?.childrenCount || 0;
    const hasChildren = childrenCount > 0;
    
    const income1 = 6500;
    const income2 = isSingle ? 0 : 5500;
    const totalIncome = income1 + income2;
    const city = 'Zürich';
    
    const annualNetIncome = totalIncome * 12;
    const grossIncomeEstimate = annualNetIncome > 0 ? annualNetIncome * 1.35 : 0;
    let monthlyTax = 0;
    if (grossIncomeEstimate > 0) {
      try {
        const taxCalculation = calculateTaxAmount(grossIncomeEstimate, city);
        monthlyTax = taxCalculation.estimatedTax > 0
          ? Math.round(taxCalculation.estimatedTax / 12)
          : 0;
      } catch (error) {
        console.error('Tax calculation failed:', error);
        monthlyTax = Math.round(totalIncome * 0.15);
      }
    }
    
    return [
      {
        id: 'income',
        type: 'income',
        items: [
          { id: 'net-income-1', monthly: income1, annual: income1 * 12, isDefault: true, defaultValue: income1 },
          { id: 'net-income-2', monthly: income2, annual: income2 * 12, isDefault: true, defaultValue: income2 },
          { id: 'additional-income', monthly: 0, annual: 0, isDefault: true, defaultValue: 0 },
          { id: 'alimony', monthly: 0, annual: 0, isDefault: true, defaultValue: 0 },
          { id: 'pension', monthly: 0, annual: 0, isDefault: true, defaultValue: 0 },
          { id: 'other-income', monthly: 0, annual: 0, isDefault: true, defaultValue: 0 }
        ]
      },
      {
        id: 'housing',
        type: 'expense',
        items: [
          { id: 'rent', monthly: isSingle ? 1400 : 1800, annual: (isSingle ? 1400 : 1800) * 12, isDefault: true, defaultValue: isSingle ? 1400 : 1800 },
          { id: 'utilities', monthly: isSingle ? 200 : 280, annual: (isSingle ? 200 : 280) * 12, isDefault: true, defaultValue: isSingle ? 200 : 280 },
          { id: 'maintenance', monthly: 0, annual: 0, isDefault: true, defaultValue: 0 }
        ]
      },
      {
        id: 'family',
        type: 'expense',
        items: [
          { id: 'childcare', monthly: hasChildren ? 1200 : 0, annual: hasChildren ? 14400 : 0, isDefault: true, defaultValue: hasChildren ? 1200 : 0 },
          { id: 'education', monthly: hasChildren ? 300 : 0, annual: hasChildren ? 3600 : 0, isDefault: true, defaultValue: hasChildren ? 300 : 0 },
          { id: 'family-other', monthly: hasChildren ? 150 : 0, annual: hasChildren ? 1800 : 0, isDefault: true, defaultValue: hasChildren ? 150 : 0 }
        ]
      },
      {
        id: 'household',
        type: 'expense',
        items: [
          { id: 'groceries', monthly: isSingle ? 400 : 650, annual: (isSingle ? 400 : 650) * 12, isDefault: true, defaultValue: isSingle ? 400 : 650 },
          { id: 'clothing', monthly: isSingle ? 150 : 250, annual: (isSingle ? 150 : 250) * 12, isDefault: true, defaultValue: isSingle ? 150 : 250 },
          { id: 'household-items', monthly: isSingle ? 100 : 150, annual: (isSingle ? 100 : 150) * 12, isDefault: true, defaultValue: isSingle ? 100 : 150 }
        ]
      },
      {
        id: 'mobility',
        type: 'expense',
        items: [
          { id: 'car', monthly: 0, annual: 0, isDefault: true, defaultValue: 0 },
          { id: 'public-transport', monthly: 250, annual: 3000, isDefault: true, defaultValue: 250 },
          { id: 'fuel', monthly: 0, annual: 0, isDefault: true, defaultValue: 0 }
        ]
      },
      {
        id: 'communication',
        type: 'expense',
        items: [
          { id: 'phone', monthly: 120, annual: 1440, isDefault: true, defaultValue: 120 },
          { id: 'internet', monthly: 80, annual: 960, isDefault: true, defaultValue: 80 },
          { id: 'tv', monthly: 60, annual: 720, isDefault: true, defaultValue: 60 }
        ]
      },
      {
        id: 'taxes',
        type: 'expense',
        items: [
          { id: 'income-tax', monthly: monthlyTax, annual: monthlyTax * 12, isDefault: true, defaultValue: monthlyTax },
          { id: 'property-tax', monthly: 0, annual: 0, isDefault: true, defaultValue: 0 },
          { id: 'other-taxes', monthly: 0, annual: 0, isDefault: true, defaultValue: 0 }
        ]
      },
      {
        id: 'health',
        type: 'expense',
        items: [
          { id: 'health-insurance', monthly: 320, annual: 3840, isDefault: true, defaultValue: 320 },
          { id: 'medical-expenses', monthly: 80, annual: 960, isDefault: true, defaultValue: 80 },
          { id: 'pharmacy', monthly: 50, annual: 600, isDefault: true, defaultValue: 50 }
        ]
      },
      {
        id: 'insurance',
        type: 'expense',
        items: [
          { id: 'liability', monthly: 40, annual: 480, isDefault: true, defaultValue: 40 },
          { id: 'household-insurance', monthly: 30, annual: 360, isDefault: true, defaultValue: 30 },
          { id: 'life-insurance', monthly: isSingle ? 0 : 120, annual: isSingle ? 0 : 1440, isDefault: true, defaultValue: isSingle ? 0 : 120 }
        ]
      },
      {
        id: 'leisure',
        type: 'expense',
        items: [
          { id: 'sports', monthly: Math.round(totalIncome * 0.03), annual: Math.round(totalIncome * 0.03 * 12), isDefault: true, defaultValue: Math.round(totalIncome * 0.03) },
          { id: 'hobbies', monthly: Math.round(totalIncome * 0.04), annual: Math.round(totalIncome * 0.04 * 12), isDefault: true, defaultValue: Math.round(totalIncome * 0.04) },
          { id: 'entertainment', monthly: Math.round(totalIncome * 0.03), annual: Math.round(totalIncome * 0.03 * 12), isDefault: true, defaultValue: Math.round(totalIncome * 0.03) }
        ]
      },
      {
        id: 'savings',
        type: 'expense',
        items: [
          { id: 'emergency-fund', monthly: Math.round(totalIncome * 0.05), annual: Math.round(totalIncome * 0.05 * 12), isDefault: true, defaultValue: Math.round(totalIncome * 0.05) },
          { id: 'retirement', monthly: Math.round(totalIncome * 0.05), annual: Math.round(totalIncome * 0.05 * 12), isDefault: true, defaultValue: Math.round(totalIncome * 0.05) },
          { id: 'investments', monthly: Math.round(totalIncome * 0.02), annual: Math.round(totalIncome * 0.02 * 12), isDefault: true, defaultValue: Math.round(totalIncome * 0.02) }
        ]
      },
      {
        id: 'miscellaneous',
        type: 'expense',
        items: [
          { id: 'gifts', monthly: 80, annual: 960, isDefault: true, defaultValue: 80 },
          { id: 'donations', monthly: 30, annual: 360, isDefault: true, defaultValue: 30 },
          { id: 'other', monthly: 60, annual: 720, isDefault: true, defaultValue: 60 }
        ]
      }
    ];
  };

  const [budgetData, setBudgetData] = useState<BudgetCategory[]>(getDefaultValues());

  const [totals, setTotals] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    annualSavings: 0
  });

  // Calculate totals whenever budget data changes
  useEffect(() => {
    const incomeCategory = budgetData.find(cat => cat.type === 'income');
    const expenseCategories = budgetData.filter(cat => cat.type === 'expense');

    const totalIncome = incomeCategory?.items.reduce((sum, item) => {
      const annual = Number(item.annual) || 0;
      return sum + annual;
    }, 0) || 0;
    
    const totalExpenses = expenseCategories.reduce((sum, category) => 
      sum + category.items.reduce((catSum, item) => {
        const annual = Number(item.annual) || 0;
        return catSum + annual;
      }, 0), 0
    );

    setTotals({
      totalIncome,
      totalExpenses,
      annualSavings: totalIncome - totalExpenses
    });
  }, [budgetData]);

  // Load user data from Memberstack
  useEffect(() => {
    const loadUserData = async () => {
      if (!memberstack || !isAuthenticated) {
        return;
      }
      
      try {
        const response = await memberstack.getMemberJSON();
        const userData = response.data || {};
        
        if (userData.budget && Array.isArray(userData.budget)) {
          const familyStatus = userData.questionnaire?.familyStatus || 'single';
          const isSingle = familyStatus === 'single';
          const childrenCount = userData.questionnaire?.childrenCount || 0;
          const hasChildren = childrenCount > 0;
          
          const updatedBudgetData = userData.budget.map((category: BudgetCategory) => {
            if (category.id === 'income') {
              return {
                ...category,
                items: category.items.map((item: BudgetItem) => {
                  if (item.id === 'net-income-2') {
                    return {
                      ...item,
                      monthly: isSingle ? 0 : item.monthly,
                      annual: isSingle ? 0 : item.annual
                    };
                  }
                  return item;
                })
              };
            }
            if (category.id === 'family') {
              return {
                ...category,
                items: category.items.map((item: BudgetItem) => {
                  if (item.id === 'childcare') {
                    return {
                      ...item,
                      monthly: hasChildren ? item.monthly : 0,
                      annual: hasChildren ? item.annual : 0
                    };
                  }
                  return item;
                })
              };
            }
            return category;
          });
          
          setBudgetData(updatedBudgetData);
          setUserData(userData);
        } else {
          const defaultData = getDefaultValues(userData.questionnaire);
          setBudgetData(defaultData);
        }
      } catch (error) {
        console.error('Failed to load user data:', error);
      }
    };

    loadUserData();
  }, [memberstack, isAuthenticated]);

  const saveToMemberstack = async () => {
    if (!memberstack) return;

    try {
      const budgetDataToSave = budgetData;
      await memberstack.updateMemberJSON({ json: { budget: budgetDataToSave } });
      setUserData(budgetDataToSave);
      setHasUnsavedChanges(false);
      
      await addActivity(memberstack, {
        type: 'budget_update',
        action: t('activity.budgetUpdated'),
        target: t('budget.title'),
        details: { categories: Object.keys(budgetDataToSave) }
      });
    } catch (error) {
      console.error('Failed to save budget data to Memberstack:', error);
    }
  };

  const handleValueChange = (categoryId: string, itemId: string, field: 'monthly' | 'annual', value: string) => {
    const numValue = parseFloat(value) || 0;
    
    setBudgetData(prev => prev.map(category => {
      if (category.id === categoryId) {
        return {
          ...category,
          items: category.items.map(item => {
            if (item.id === itemId) {
              const updatedItem = { ...item, [field]: numValue, isDefault: false };
              if (field === 'monthly') {
                updatedItem.annual = numValue * 12;
              } else {
                updatedItem.monthly = numValue / 12;
              }
              return updatedItem;
            }
            return item;
          })
        };
      }
      return category;
    }));
    
    setHasUnsavedChanges(true);
  };

  const resetToDefaults = () => {
    setBudgetData(getDefaultValues());
    setHasUnsavedChanges(true);
  };

  const handleSave = () => {
    saveToMemberstack();
  };

  // Auto-save when data changes
  useEffect(() => {
    if (hasUnsavedChanges && isAuthenticated) {
      const timeoutId = setTimeout(() => {
        saveToMemberstack();
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [budgetData, hasUnsavedChanges, isAuthenticated]);

  if (isLoading) {
    return (
      <div className="budget-loading">
        <div className="text-center">
          <div className="budget-spinner"></div>
          <p className="text-gray-600">{t('budget.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="budget-loading">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('budget.loginRequired')}</h1>
          <p className="text-gray-600 mb-6">{t('budget.loginRequired.description')}</p>
          <Button onClick={() => window.location.href = '/login'}>
            {t('budget.loginButton')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="budget-page">
      <div className="budget-container">
        <div className="budget-header-section">
          <div className="budget-header-main">
            <div>
              <h1 className="budget-title">{t('budget.title')}</h1>
              <p className="budget-subtitle">{t('budget.subtitle')}</p>
              {hasUnsavedChanges && (
                <p className="budget-unsaved-notice">• {t('budget.unsavedChanges')}</p>
              )}
            </div>
            <div className="budget-action-buttons">
              <Button variant="outline" onClick={resetToDefaults} className="font-light">
                <RotateCcw className="icon-sm mr-2" />
                {t('budget.reset')}
              </Button>
              <Button variant="outline" onClick={handleSave} disabled={!hasUnsavedChanges} className="font-light">
                <Save className="icon-sm mr-2" />
                {hasUnsavedChanges ? t('budget.save') : t('budget.saved')}
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="budget-summary-card">
          <div className="budget-summary-grid">
            <div className="budget-summary-item budget-summary-item-border-right">
              <div className="budget-summary-header">
                <div className="budget-summary-icon-box">
                  <TrendingUp className="budget-summary-icon" />
                </div>
                <div>
                  <h3 className="budget-summary-title">{t('budget.income')}</h3>
                </div>
              </div>
              <div className="budget-summary-value">
                {totals.totalIncome.toLocaleString('de-CH')} {t('common.chf')}
              </div>
              <p className="budget-summary-label">{t('budget.annual')}</p>
            </div>

            <div className="budget-summary-item budget-summary-item-border-right">
              <div className="budget-summary-header">
                <div className="budget-summary-icon-box">
                  <TrendingDown className="budget-summary-icon" />
                </div>
                <div>
                  <h3 className="budget-summary-title">{t('budget.expenses')}</h3>
                </div>
              </div>
              <div className="budget-summary-value">
                {totals.totalExpenses.toLocaleString('de-CH')} {t('common.chf')}
              </div>
              <p className="budget-summary-label">{t('budget.annual')}</p>
            </div>

            <div className="budget-summary-item">
              <div className="budget-summary-header">
                <div className="budget-summary-icon-box">
                  <PiggyBank className="budget-summary-icon" />
                </div>
                <div>
                  <h3 className="budget-summary-title">{t('budget.savings')}</h3>
                </div>
              </div>
              <div className="budget-summary-value">
                {totals.annualSavings.toLocaleString('de-CH')} {t('common.chf')}
              </div>
              <p className="budget-summary-label">
                {totals.annualSavings >= 0 ? `+${((totals.annualSavings / totals.totalIncome) * 100).toFixed(1)}%` : `${((totals.annualSavings / totals.totalIncome) * 100).toFixed(1)}%`} {t('budget.perYear')}
              </p>
            </div>
          </div>
        </div>

        {/* Budget Categories */}
        <div className="budget-categories-card">
          {budgetData.map((category, categoryIndex) => (
            <div key={category.id}>
              <div className="budget-category-section">
                <h2 className="budget-category-title">
                  {t(`budget.categories.${category.id}`)}
                  {category.type === 'income' && (
                    <Badge variant="outline" className="budget-badge-income">
                      {t('budget.income')}
                    </Badge>
                  )}
                  {category.type === 'expense' && (
                    <Badge variant="outline" className="budget-badge-expense">
                      {t('budget.expenses')}
                    </Badge>
                  )}
                </h2>
                
                <div className="budget-items-grid-header">
                  <div className="budget-grid-item-label">{t('budget.items.' + category.id)}</div>
                  <div className="budget-grid-label-center">{t('budget.monthly')}</div>
                  <div className="budget-grid-label-center">{t('budget.annual')}</div>
                  <div className="budget-grid-label-center">{t('budget.standard')}</div>
                </div>
                
                <div className="budget-items-list">
                  {category.items.map((item) => (
                    <div key={item.id} className="budget-item-row">
                      <div className="budget-item-info">
                        <div className="budget-item-label">
                          {t(`budget.items.${item.id}`)}
                          {item.isDefault && item.defaultValue && (
                            <Badge variant="secondary" className="budget-badge-default">
                              {t('budget.standard')}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div>
                        <Input
                          id={`${category.id}-${item.id}-monthly`}
                          type="number"
                          step="0.01"
                          value={item.monthly || ''}
                          onChange={(e) => handleValueChange(category.id, item.id, 'monthly', e.target.value)}
                          placeholder="0.00"
                          className="budget-input"
                        />
                        <div className="budget-input-label">{t('budget.monthly')}</div>
                      </div>
                      <div>
                        <Input
                          id={`${category.id}-${item.id}-annual`}
                          type="number"
                          step="0.01"
                          value={item.annual || ''}
                          onChange={(e) => handleValueChange(category.id, item.id, 'annual', e.target.value)}
                          placeholder="0.00"
                          className="budget-input"
                        />
                        <div className="budget-input-label">{t('budget.annual')}</div>
                      </div>
                      <div className="text-center">
                        <div className="budget-value-display">
                          {item.annual?.toLocaleString('de-CH')} {t('common.chf')}
                        </div>
                        {item.isDefault && item.defaultValue && (
                          <div className="budget-default-value">
                            {t('budget.standard')}: {item.defaultValue.toLocaleString('de-CH')} {t('common.chf')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {categoryIndex < budgetData.length - 1 && (
                <div className="budget-category-divider"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BudgetCalculatorApp;